/**
 * @features/dashboard/components/DashboardLayout.jsx
 * --------------------------------------------------
 * Shared shell for every role's dashboard page. The site's global Header
 * and (mobile) bottom nav are already mounted in App.js; this layout sits
 * BELOW them and provides:
 *
 *   Desktop (≥md):
 *     • optional left sidebar
 *     • title row with optional actions slot
 *     • content area with consistent gutter / max-width
 *
 *   Mobile (<md):
 *     • sticky MobileDashboardHeader (hamburger → MobileMoreSheet)
 *     • optional MobileQuickActions horizontal pill row
 *     • scroll-safe content container with bottom-nav clearance
 *
 *   <DashboardLayout
 *     title="Dashboard"
 *     subtitle="Optional second line"
 *     actions={<NewBtn/>}                       // desktop title-row right slot
 *     mobileHeaderActions={<BellIcon/>}         // mobile header right slot (optional)
 *     quickActions={[{id, label, icon, to}]}    // mobile pill row (optional)
 *   >
 *     <StatCard ... />
 *     <RecentItemsList ... />
 *   </DashboardLayout>
 *
 * The layout reads the current user's persona automatically; pass
 * `persona` explicitly only for previews / impersonation flows.
 *
 * v2.71.27 — Initial.
 * v2.71.35 — Explicit grid-cols-1 on <md to prevent overflow.
 * v2.71.38 — Added unified mobile shell (MobileDashboardHeader +
 *            MobileMoreSheet + MobileQuickActions). Desktop layout
 *            untouched. Zero breaking change for existing pages —
 *            new mobile props are opt-in.
 */

import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { DashboardSidebar } from "./DashboardSidebar";
import { MobileDashboardHeader } from "./mobile/MobileDashboardHeader";
import { MobileMoreSheet } from "./mobile/MobileMoreSheet";
import { MobileQuickActions } from "./mobile/MobileQuickActions";
import { useAuth } from "../../../lib/AuthContext";
import { resolvePersona } from "../config/permissions";
import { quickActionsFor } from "../config/navigationConfig";

export function DashboardLayout({
  title,
  subtitle,
  actions,
  mobileHeaderActions,
  quickActions,
  persona: personaProp,
  sidebar = true,
  children,
}) {
  const { user } = useAuth();
  const persona = personaProp || resolvePersona(user);
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // v2.71.39 — If the page didn't pass an explicit `quickActions` prop,
  // fall back to the centralized `QUICK_ACTIONS_CONFIG` entry for the
  // current route. Pages still win when they pass their own array (or
  // explicit `null` to suppress).
  const resolvedQuickActions =
    quickActions !== undefined ? quickActions : quickActionsFor(persona, pathname);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="dashboard-layout">
      {/* Mobile-only sticky header. Hidden on desktop. */}
      <MobileDashboardHeader
        title={title}
        subtitle={subtitle}
        onMenuOpen={() => setMoreOpen(true)}
        rightActions={mobileHeaderActions}
      />

      {/* Mobile-only "More" sheet — drives the full nav. */}
      <MobileMoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        persona={persona}
      />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8 pb-24 md:pb-8">
        {/* Mobile quick actions pill row (config-driven, opt-in via prop). */}
        <MobileQuickActions actions={resolvedQuickActions} />

        {/* Desktop-only title row. Mobile shows title in the sticky header. */}
        {(title || subtitle || actions) && (
          <div className="hidden md:flex flex-wrap items-end justify-between gap-3 mb-5 sm:mb-6">
            <div className="min-w-0">
              {title && (
                <h1
                  className="font-heading text-[20px] sm:text-[24px] font-semibold text-[#09090B] tracking-tight"
                  data-testid="dashboard-title"
                >
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-[13px] text-[#71717A] mt-0.5">{subtitle}</p>
              )}
            </div>
            {actions && (
              <div className="flex items-center gap-2" data-testid="dashboard-actions">
                {actions}
              </div>
            )}
          </div>
        )}
        {/* v2.71.40 — Page's `actions` prop renders ONLY in the desktop
            title row. On mobile, `MobileQuickActions` (config-driven pill
            row) already exposes the same high-frequency CTAs; rendering
            `actions` again on phones produced a duplicate "Add Property"
            button stack. If a page needs phone-specific actions, it should
            pass them via `mobileHeaderActions` (sits in the sticky header)
            or `quickActions` (pill row override). */}

        {/* Sidebar + content grid. Sidebar hidden on <md; the mobile
            header + bottom-nav + More sheet cover navigation on phones. */}
        <div
          className={
            sidebar
              ? "grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] lg:grid-cols-[220px_minmax(0,1fr)] gap-4 lg:gap-6"
              : ""
          }
        >
          {sidebar && (
            <aside className="hidden md:block min-w-0">
              <DashboardSidebar persona={persona} />
            </aside>
          )}
          <main
            className="min-w-0 max-w-full overflow-x-hidden"
            data-testid="dashboard-main"
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
